import { Component, Injector, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BaseModalComponent } from '@card-software/library';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { LocationNode, LocationType } from '../../../../models/location';
import { LocationProxyService } from '../../../../proxies/inventory/location.proxy.service';
import { LocalHelperService } from '../../../../services/local-helper.service';
import { Translation } from '../../../../models/translation';
import _ from 'lodash';

@Component({
  selector: 'app-location-modal',
  templateUrl: './location-modal.component.html',
  styleUrls: ['./location-modal.component.scss'],
})
export class LocationModalComponent extends BaseModalComponent<LocationNode | null> implements OnInit {
  @Input() parentLocation: LocationNode | null = null;
  @Input() locationToEdit: LocationNode | null = null;

  isLoading = false;
  submittedForm = false;
  showTranslateInputs = false;

  locationNameFormGroup: FormGroup = new FormGroup({
    en: new FormControl<string | null>(null, [Validators.required]),
    es: new FormControl<string | null>(null),
  });

  locationDescriptionFormGroup: FormGroup = new FormGroup({
    en: new FormControl<string | null>(null),
    es: new FormControl<string | null>(null),
  });

  locationFormGroup: FormGroup = new FormGroup({
    code: new FormControl('', [Validators.required]),
    name: this.locationNameFormGroup,
    description: this.locationDescriptionFormGroup,
    isStorable: new FormControl(false),
    itemCodeStored: new FormControl<string | null>(null),
    maxUnits: new FormControl<number | null>(null),
  });

  childType: LocationType | null = null;
  childTypeName = '';
  fullLocationCodePreview = '';
  LocationType = LocationType;

  translations: Record<string, Translation> = {
    Success_Save_Message: {
      en: 'Location {{code}} saved successfully',
      es: 'Ubicación {{code}} guardada exitosamente',
    },
  };

  get isEditing(): boolean {
    return !_.isNil(this.locationToEdit);
  }

  constructor(
    injector: Injector,
    private locationProxyService: LocationProxyService,
    private messageService: MessageService,
    private translateService: TranslateService,
    public localHelperTranslate: LocalHelperService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    if (this.parentLocation) {
      this.childType = this.getChildType(this.parentLocation.type);
      if (this.childType !== null) {
        this.childTypeName = LocationType[this.childType];
      }

      // Only Bin level is typically storable
      if (this.childType === LocationType.Bin) {
        this.locationFormGroup.patchValue({ isStorable: true });
      }
    } else {
      // Creating a top-level site
      this.childType = LocationType.Site;
      this.childTypeName = LocationType[LocationType.Site];
    }

    if (this.locationToEdit) {
      this.patchFormWithLocation(this.locationToEdit);
    }

    // Watch code changes to update preview
    this.locationFormGroup.get('code')?.valueChanges.subscribe((code) => {
      this.updateLocationCodePreview(code);
    });
  }

  getChildType(parentType: LocationType): LocationType | null {
    const typeMap: Record<LocationType, LocationType | null> = {
      [LocationType.Site]: LocationType.Warehouse,
      [LocationType.Warehouse]: LocationType.Zone,
      [LocationType.Zone]: LocationType.Aisle,
      [LocationType.Aisle]: LocationType.Rack,
      [LocationType.Rack]: LocationType.Bay,
      [LocationType.Bay]: LocationType.Level,
      [LocationType.Level]: LocationType.Bin,
      [LocationType.Bin]: null, // No children allowed
    };

    return typeMap[parentType];
  }

  updateLocationCodePreview(childCode: string): void {
    if (this.isEditing && this.locationToEdit) {
      // Code is immutable once created, so the preview must stay the existing
      // hierarchical LocationCode regardless of whether parentLocation was passed in.
      const segments = this.locationToEdit.locationCode.split('.');
      segments[segments.length - 1] = childCode;
      this.fullLocationCodePreview = segments.join('.');
    } else if (this.parentLocation && childCode) {
      this.fullLocationCodePreview = `${this.parentLocation.locationCode}.${childCode}`;
    } else {
      this.fullLocationCodePreview = childCode;
    }
  }

  patchFormWithLocation(location: LocationNode): void {
    this.locationFormGroup.patchValue({
      code: location.code,
      isStorable: location.isStorable,
      itemCodeStored: location.itemCodeStored,
      maxUnits: location.maxUnits,
    });

    // Code is immutable once a location is created — the API rejects changes to it.
    this.locationFormGroup.get('code')?.disable();

    this.locationNameFormGroup.patchValue({
      en: location.name.en,
      es: location.name.es,
    });

    this.locationDescriptionFormGroup.patchValue({
      en: location.description?.en || '',
      es: location.description?.es || '',
    });

    this.updateLocationCodePreview(location.code);
  }

  onSave(): void {
    this.submittedForm = true;

    if (this.locationFormGroup.valid && this.locationNameFormGroup.valid) {
      this.isLoading = true;

      // getRawValue() includes the disabled 'code' control (disabled while editing);
      // FormGroup.value would silently drop it and send code: undefined.
      const rawFormValue = this.locationFormGroup.getRawValue();

      const locationNode: Partial<LocationNode> = {
        type: this.childType!,
        code: rawFormValue.code,
        name: this.locationNameFormGroup.value,
        description: this.locationDescriptionFormGroup.value || { en: '', es: '' },
        parentCode: this.parentLocation?.locationCode,
        locationCode: this.fullLocationCodePreview,
        isStorable: rawFormValue.isStorable,
        itemCodeStored: rawFormValue.itemCodeStored,
        maxUnits: rawFormValue.maxUnits,
      };

      if (this.locationToEdit) {
        locationNode.id = this.locationToEdit.id;
        locationNode.path = this.locationToEdit.path;
        locationNode.parentCode = this.locationToEdit.parentCode;
        locationNode.type = this.locationToEdit.type;
        // Update existing location
        this.locationProxyService.updateLocation(this.locationToEdit.id, locationNode as LocationNode).subscribe({
          next: (response) => {
            this.isLoading = false;
            if (response.success) {
              this.close({
                action: 'save',
                data: response.data,
              });
              this.messageService.add({
                severity: 'success',
                summary: this.translations['Success_Save_Message'][this.translateService.currentLang as keyof Translation].replace(
                  '{{code}}',
                  locationNode.locationCode!,
                ),
                key: 'br',
                life: 2500,
              });
            }
          },
          error: (error) => {
            this.isLoading = false;
            console.error('Error updating location:', error);
          },
        });
      } else {
        // Create new location
        this.locationProxyService.createLocation(locationNode as LocationNode).subscribe({
          next: (response) => {
            this.isLoading = false;
            if (response.success && response.data) {
              this.close({
                action: 'save',
                data: response.data,
              });
              this.messageService.add({
                severity: 'success',
                summary: this.translations['Success_Save_Message'][this.translateService.currentLang as keyof Translation].replace(
                  '{{code}}',
                  response.data.code,
                ),
                key: 'br',
                life: 2500,
              });
            }
          },
          error: (error) => {
            this.isLoading = false;
            console.error('Error creating location:', error);
          },
        });
      }
    }
  }

  onClose(): void {
    this.close({ action: 'close', data: null });
  }
}
